/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useStyles } from './style';
import { Api, ApplicationState, KeyType, UserReducer } from '../../../data/interfaces';
import APIKeyGenerationWizard from './APIKeyGenerationWizard';
import ApiKeysList, { ViewType } from '../../shared/ApiKeys/List';
import { fetchApiKeysList } from '../../../data/operations';
import GenerateFirstApiKey from './GenerateFirstApiKey';
import Loader from '../../shared/ApiKeys/Loader';

interface ApiKeyCredentialsProps {
    apiInfo: Api,
    keyType: KeyType,
    userReducer: UserReducer,

    fetchApiKeys: (apiId: string, applicationId: string, organizationId: string, keyType: string) => void,
}

function APIKeyCredentials(props: ApiKeyCredentialsProps) {
    const { apiInfo, keyType, userReducer, fetchApiKeys } = props;
    const classes = useStyles();

    const { selectedOrg} = userReducer;

    const [isApiKeysLoaded, setIsApiKeysLoaded] = useState(false);
    const [apiKeyGenerationWizardOpen, setApiKeyGenerationWizardOpen] = useState(false);

    useEffect(() => {
        if (!isApiKeysLoaded) {
            fetchApiKeys(apiInfo.id, "", selectedOrg.uuid, keyType);
            setIsApiKeysLoaded(true);
        }
    }, [isApiKeysLoaded , apiInfo, keyType]);

    const onClose = () => {
        fetchApiKeys(apiInfo.id, "", selectedOrg.uuid, keyType);
        setApiKeyGenerationWizardOpen(false);
    }

    const openApiKeyGenerationWizard = () => {
        setApiKeyGenerationWizardOpen(true);
    }

    return (
        <main className={classes.content}>
            <Loader />
            <GenerateFirstApiKey 
                apiInfo={apiInfo} 
                onGenerateKey={openApiKeyGenerationWizard} 
                keyType={keyType}
            />
            <APIKeyGenerationWizard
                keyType={keyType}
                open={apiKeyGenerationWizardOpen}
                onClose={onClose}
            />
            <ApiKeysList 
                onClose={onClose} 
                viewType={ViewType.APIView} 
                onGenerateApiKey={openApiKeyGenerationWizard} 
            />
        </main>
    );
}

const mapDispatchToProps = {
    fetchApiKeys: fetchApiKeysList,
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiInfo: state.apiReducer.api,
        userReducer: state.userReducer,
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(APIKeyCredentials);
